'use client';

import { LDProvider as _LDProvider, type LDContext, useLDClient } from 'launchdarkly-react-client-sdk';
import { isEqual, noop } from 'lodash';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

const IDENTIFY_DEBOUNCE_MS = 250;

export type LaunchDarklyContextValue = {
  identify: (context: LDContext) => void;
  /**
   * True once the latest `identify` attempt has finished (success or failure).
   * Use this to stop waiting; do not treat it as proof that flags are for the latest context.
   */
  isReady: boolean;
  /**
   * True only when the latest `identify` succeeded (or the client already had that context).
   * Use this before trusting workspace-targeted flags for entitlement.
   */
  hasIdentified: boolean;
};

export const LAUNCH_DARKLY_CONTEXT_DEFAULT_VALUE: LaunchDarklyContextValue = {
  hasIdentified: false,
  identify: noop,
  isReady: false,
};

export const LaunchDarklyContext = createContext<LaunchDarklyContextValue>(LAUNCH_DARKLY_CONTEXT_DEFAULT_VALUE);

export type LaunchDarklyContextProviderProps = {
  children: ReactNode;
};

/** Inner provider that sets up the identify context (requires the SDK's LDProvider as ancestor) */
const LaunchDarklyContextProvider = memo(({ children }: LaunchDarklyContextProviderProps) => {
  const ldClient = useLDClient();
  const userRef = useRef<LDContext>();
  const identifyGenerationRef = useRef(0);
  const [isReady, setIsReady] = useState(false);
  const [hasIdentified, setHasIdentified] = useState(false);

  const [debouncedIdentify] = useDebouncedCallback(async () => {
    if (!userRef.current || !ldClient) {
      return;
    }

    const contextToIdentify = userRef.current;
    const generation = identifyGenerationRef.current;
    const isLatest = () => generation === identifyGenerationRef.current && isEqual(userRef.current, contextToIdentify);

    if (isEqual(contextToIdentify, ldClient.getContext())) {
      if (isLatest()) {
        setHasIdentified(true);
        setIsReady(true);
      }

      return;
    }

    let identifiedSuccessfully = false;

    try {
      await ldClient.identify(contextToIdentify);
      identifiedSuccessfully = true;
    } catch (error) {
      const typedError = convertUnknownToError(error);

      // ignore network error to prevent unhandled promise being thrown and logging that to bugsnag
      if (typedError.message !== 'network error (Error)') {
        reportErrorToBugsnag({
          context: 'LaunchDarkly identify failed',
          error: typedError,
        });
      }
    }

    // Finish the attempt even on failure so consumers don't hang; only trust flags on success.
    if (isLatest()) {
      setHasIdentified(identifiedSuccessfully);
      setIsReady(true);
    }
  }, IDENTIFY_DEBOUNCE_MS);

  // When ldClient becomes available after SDK init, identify with any pending context.
  // The debounced identify may have fired before ldClient was ready.
  useEffect(() => {
    if (ldClient && userRef.current) {
      debouncedIdentify();
    }
  }, [ldClient, debouncedIdentify]);

  const identify = useCallback(
    (user: LDContext & { platform?: string }) => {
      const userObject = {
        appVersion: process.env.APP_VERSION,
        kind: 'user',
        platform: 'plugin',
        ...userRef.current,
        ...user,
      };

      if (!isEqual(userObject, userRef.current)) {
        userRef.current = userObject;
        identifyGenerationRef.current += 1;
        setHasIdentified(false);
        setIsReady(false);
        debouncedIdentify();
      }
    },
    [debouncedIdentify],
  );

  const value = useMemo(() => ({ hasIdentified, identify, isReady }), [identify, isReady, hasIdentified]);

  return <LaunchDarklyContext.Provider value={value}>{children}</LaunchDarklyContext.Provider>;
});

LaunchDarklyContextProvider.displayName = 'LaunchDarklyContextProvider';

export type LaunchDarklyProviderProps = {
  children: ReactNode;
  clientSideID: string | undefined;
};

/** Combined LaunchDarkly provider: initializes the SDK and provides the identify context */
export const LaunchDarklyProvider = memo(({ children, clientSideID }: LaunchDarklyProviderProps) => {
  if (!clientSideID) {
    console.error('No LaunchDarkly key was provided!');

    return <>{children}</>;
  }

  return (
    <_LDProvider clientSideID={clientSideID}>
      <LaunchDarklyContextProvider>{children}</LaunchDarklyContextProvider>
    </_LDProvider>
  );
});

LaunchDarklyProvider.displayName = 'LaunchDarklyProvider';

export const useLaunchDarklyContext = () => {
  const context = useContext(LaunchDarklyContext);

  if (context === LAUNCH_DARKLY_CONTEXT_DEFAULT_VALUE) {
    const error = new Error('useLaunchDarklyContext must be used within a LaunchDarklyProvider');

    reportErrorToBugsnag({
      context: 'useLaunchDarklyContext must be used within a LaunchDarklyProvider',
      error,
    });
  }

  return context;
};
